import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2, 
  ArrowRight, 
  Rocket, 
  Server, 
  Zap, 
  Shield, 
  Cpu,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import CortexShowcase from '../components/CortexShowcase';

export const Solutions = () => {
  const { t, i18n } = useTranslation();
  const [activeLayer, setActiveLayer] = useState<'foundation' | 'launch' | 'vector'>('launch');


  const anchors = useMemo(
    () => [
      { id: 'operational-layers', label: t('solutions.anchors.layers') as string },
      { id: 'cortex-ai', label: t('solutions.anchors.cortex') as string },
      { id: 'plans', label: t('solutions.anchors.plans') as string },
      { id: 'promise', label: t('solutions.anchors.promise') as string },
      { id: 'test-drive', label: t('solutions.anchors.testDrive') as string },
      { id: 'faq', label: t('solutions.anchors.faq') as string },
    ],
    [t, i18n.language]
  );

  const layers = useMemo(() => {
    const keys = ['foundation', 'launch', 'vector'] as const;
    return keys.map((key) => {
      const features = t(`solutions.layers.items.${key}.features`, {
        returnObjects: true,
        defaultValue: [],
      }) as string[];
      const badge = t(`solutions.layers.items.${key}.badge`, { defaultValue: '' }) as string;
      const priceNote = t(`solutions.layers.items.${key}.priceNote`, { defaultValue: '' }) as string;

      const sanitizeOptional = (value: string, keyPath: string) =>
        value && value !== keyPath ? value : '';

      return {
        code: key,
        title: t(`solutions.layers.items.${key}.title`),
        tagline: t(`solutions.layers.items.${key}.tagline`),
        description: t(`solutions.layers.items.${key}.description`),
        price: t(`solutions.layers.items.${key}.price`),
        cta: t(`solutions.layers.items.${key}.cta`),
        features: Array.isArray(features) ? features : [],
        badge: sanitizeOptional(badge, `solutions.layers.items.${key}.badge`) || undefined,
        priceNote:
          sanitizeOptional(priceNote, `solutions.layers.items.${key}.priceNote`) || undefined,
      };
    });
  }, [t, i18n.language]);

  const plans = useMemo(() => {
    const plansArray = (t('solutions.plans.items', { returnObjects: true }) as any[]) || [];
    return plansArray.sort((a, b) => {
      if (a.code === 'foundation' && b.code !== 'foundation') return -1;
      if (b.code === 'foundation' && a.code !== 'foundation') return 1;
      if (a.code === 'launch' && b.code !== 'foundation' && b.code !== 'launch') return -1;
      if (b.code === 'launch' && a.code !== 'foundation' && a.code !== 'launch') return 1;
      return 0;
    });
  }, [t, i18n.language]);

  const faqItems = (t('solutions.faq.items', { returnObjects: true }) as any[]) || [];
  const heroTitle = t('solutions.hero.title') as string;
  const heroTitleSegments = useMemo(
    () => heroTitle.split('.').map((segment) => segment.trim()).filter(Boolean),
    [heroTitle]
  );
  const primaryHeroTitle = heroTitleSegments[0] || heroTitle;
  const secondaryHeroTitle = heroTitleSegments[1];

  return (
    <div className="pt-24 bg-slate-950 text-slate-200 min-h-screen selection:bg-cyan-500/30 font-sans">
      <SEO
        title={`${t('navigation.solutions') || 'Solutions'} – Victor Lazari`}
        description={t('solutions.hero.subtitle') as string}
        canonical={"https://victorlazari.com/solutions"}
        image={"https://raw.githubusercontent.com/VictorLazari-lab/victorlazari.com/VictorLazari-Media/varienda%20minimalist%20logo.png"}
        type="website"
      />
      
      {/* Sticky mini-nav */}
      <div className="sticky top-16 z-30 border-b border-white/5 bg-slate-950/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-4 overflow-x-auto py-2 no-scrollbar">
            {anchors.map((a) => (
              <a key={a.id} href={`#${a.id}`} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white rounded-md hover:bg-white/5 transition-colors whitespace-nowrap font-mono uppercase tracking-wider">
                {a.label}
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* Operational Layers - The "Infrastructure Builder" */}
      <section id="operational-layers" className="relative overflow-hidden">
        {/* Background Grid & Effects */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.05)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-blue-900/10 rounded-full blur-[100px] pointer-events-none mix-blend-screen" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto mb-20"
          >
            
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-white leading-none mb-8">
              {primaryHeroTitle}
              {secondaryHeroTitle ? (
                <>
                  .<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600">
                    {secondaryHeroTitle}.
                  </span>
                </>
              ) : (
                '.'
              )}
            </h1>
            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mx-auto">
              {t('solutions.hero.subtitle')}
            </p>
          </motion.div>

          {/* Interactive Layer Selector */}
          <div className="grid lg:grid-cols-[1fr_1.5fr] gap-12 items-start">
             {/* Layer Tabs */}
             <div className="space-y-4">
                {layers.map((layer) => {
                   const isActive = activeLayer === layer.code;
                   const isLaunch = layer.code === 'launch';
                   const isVector = layer.code === 'vector';
                   
                   return (
                      <div 
                        key={layer.code}
                        onClick={() => setActiveLayer(layer.code as any)}
                        className={`group cursor-pointer relative p-6 rounded-2xl border transition-all duration-300 ${
                           isActive 
                              ? isLaunch 
                                 ? 'bg-cyan-950/30 border-cyan-500/50 shadow-[0_0_30px_rgba(6,182,212,0.15)]' 
                                 : isVector
                                    ? 'bg-purple-950/30 border-purple-500/50 shadow-[0_0_30px_rgba(168,85,247,0.15)]'
                                    : 'bg-slate-900 border-slate-700'
                              : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                        }`}
                      >
                         {isActive && (
                            <motion.div 
                               layoutId="activeGlow"
                               className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl ${
                                  isLaunch ? 'bg-cyan-500' : isVector ? 'bg-purple-500' : 'bg-slate-500'
                               }`}
                            />
                         )}
                         
                         <div className="flex justify-between items-start mb-2">
                            <span className={`text-xs font-mono uppercase tracking-widest ${
                               isActive 
                                  ? isLaunch ? 'text-cyan-400' : isVector ? 'text-purple-400' : 'text-slate-400'
                                  : 'text-slate-600'
                            }`}>
                               {layer.tagline}
                            </span>
                            {layer.badge && (
                               <span className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-white font-bold">
                                  {layer.badge}
                               </span>
                            )}
                         </div>
                         
                         <h3 className={`text-2xl font-bold mb-2 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-slate-200'}`}>
                            {layer.title}
                         </h3>
                         
                         <div className="flex items-baseline gap-2">
                            <span className={`text-xl font-mono ${isActive ? 'text-white' : 'text-slate-500'}`}>
                               {layer.price}
                            </span>
                         </div>
                      </div>
                   );
                })}
             </div>

             {/* Layer Preview (The "Hologram") */}
             <div className="relative min-h-[600px] bg-slate-900 rounded-[2rem] border border-slate-800 p-8 overflow-hidden flex flex-col">
                {/* Dynamic Background based on active layer */}
                <div className={`absolute inset-0 transition-opacity duration-700 ${activeLayer === 'launch' ? 'opacity-20 bg-cyan-900/20' : 'opacity-0'}`} />
                <div className={`absolute inset-0 transition-opacity duration-700 ${activeLayer === 'vector' ? 'opacity-20 bg-purple-900/20' : 'opacity-0'}`} />
                
                <AnimatePresence mode="wait">
                   {layers.map((layer) => {
                      if (layer.code !== activeLayer) return null;
                      const isLaunch = layer.code === 'launch';
                      const isVector = layer.code === 'vector';
                      const accentColor = isLaunch ? 'text-cyan-400' : isVector ? 'text-purple-400' : 'text-slate-400';
                      const btnColor = isLaunch ? 'bg-cyan-500 hover:bg-cyan-400' : isVector ? 'bg-purple-600 hover:bg-purple-500' : 'bg-slate-700 hover:bg-slate-600';

                      return (
                         <motion.div
                            key={layer.code}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.4 }}
                            className="relative z-10 flex flex-col h-full"
                         >
                            <div className="mb-8">
                               <div className={`inline-flex p-3 rounded-xl bg-slate-950 border border-slate-800 mb-6 ${accentColor}`}>
                                  {isLaunch ? <Rocket size={32} /> : isVector ? <Cpu size={32} /> : <Server size={32} />}
                               </div>
                               <h2 className="text-3xl font-bold text-white mb-4">{layer.description}</h2>
                               <div className="h-1 w-20 bg-slate-800 rounded-full overflow-hidden">
                                  <div className={`h-full ${isLaunch ? 'bg-cyan-500 w-2/3' : isVector ? 'bg-purple-500 w-full' : 'bg-slate-500 w-1/3'}`} />
                               </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 flex-1">
                               {layer.features.map((feature, i) => (
                                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                                     <CheckCircle2 className={`shrink-0 w-5 h-5 ${accentColor}`} />
                                     <span className="text-sm text-slate-300">{feature}</span>
                                  </div>
                               ))}
                            </div>

                            <div className="mt-auto pt-8 border-t border-slate-800 flex flex-col sm:flex-row gap-4 items-center">
                               <Link 
                                  to={layer.code === 'vector' ? '/vector' : '/contact'}
                                  className={`w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-all hover:scale-[1.02] flex items-center justify-center gap-2 ${btnColor}`}
                               >
                                  {layer.cta} <ArrowRight size={18} />
                               </Link>
                               <p className="text-xs text-slate-500 max-w-xs">
                                  {layer.priceNote || "Setup in 7-14 giorni lavorativi. Garanzia soddisfatti o rimborsati."}
                               </p>
                            </div>
                         </motion.div>
                      );
                   })}
                </AnimatePresence>
             </div>
          </div>
        </div>
      </section>

      {/* Cortex AI Showcase */}
      <CortexShowcase />

      {/* Deep Dive Comparison (The "Specs Sheet") */}
      <section id="plans" className="relative py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-white mb-4">{t('solutions.deepDive.title')}</h2>
              <p className="text-slate-400">{t('solutions.deepDive.subtitle')}</p>
           </div>

           <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan) => {
                 const isLaunch = plan.code === 'launch';
                 const isVector = plan.code === 'vector';
                 
                 return (
                    <div key={plan.code} className={`relative p-8 rounded-3xl border bg-slate-900/50 flex flex-col ${
                       isLaunch ? 'border-cyan-500/30' : 'border-slate-800'
                    }`}>
                       {isLaunch && (
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                             Consigliato
                          </div>
                       )}
                       
                       <div className="mb-8 text-center">
                          <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                          <div className="text-3xl font-mono text-slate-200">{plan.price}</div>
                          <p className="text-xs text-slate-500 mt-2">{plan.for}</p>
                       </div>

                       <div className="space-y-6 flex-1">
                          {/* Includes */}
                          <div className="space-y-3">
                             <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Core Features</p>
                             {(plan.includes || []).map((inc: string, i: number) => (
                                <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                   <CheckCircle2 className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />
                                   <span>{inc.split(':')[0]}</span>
                                </div>
                             ))}
                          </div>
                          
                          {/* Automations */}
                          {(plan.automations && plan.automations.length > 0) && (
                             <div className="space-y-3">
                                <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Automazione</p>
                                {plan.automations.map((aut: string, i: number) => (
                                   <div key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                      <Zap className={`w-4 h-4 shrink-0 mt-0.5 ${isLaunch ? 'text-cyan-500' : 'text-slate-600'}`} />
                                      <span>{aut}</span>
                                   </div>
                                ))}
                             </div>
                          )}
                       </div>

                       <div className="mt-8 pt-8 border-t border-slate-800">
                          <Link
                             to={isVector ? '/vector' : '/contact'}
                             className={`block w-full py-3 rounded-xl text-center font-bold transition-colors ${
                                isLaunch 
                                   ? 'bg-cyan-500 text-black hover:bg-cyan-400' 
                                   : 'bg-slate-800 text-white hover:bg-slate-700'
                             }`}
                          >
                             Seleziona
                          </Link>
                       </div>
                    </div>
                 );
              })}
           </div>
        </div>
      </section>

      {/* The Guarantee (System Integrity) */}
      <section id="promise" className="py-20 bg-slate-900/30 border-y border-slate-800">
         <div className="max-w-4xl mx-auto px-6 text-center">
            <Shield className="w-16 h-16 text-emerald-500 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold text-white mb-6">{t('solutions.promise.title')}</h2>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
               "{t('solutions.promise.headline')}"
            </p>
            <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-2xl p-6 inline-block">
               <p className="text-emerald-400 font-mono text-sm mb-2">PROTOCOL: ROI_GUARANTEE_v1</p>
               <p className="text-slate-300 text-sm max-w-lg mx-auto">
                  {t('solutions.promise.guarantee')}
               </p>
            </div>
         </div>
      </section>

      {/* FAQ - The Knowledge Base */}
      <section id="faq" className="py-24 max-w-3xl mx-auto px-6">
         <div className="text-center mb-12">
            
            <h2 className="text-3xl font-bold text-white">{t('solutions.faq.title')}</h2>
         </div>
         
         <div className="space-y-4">
            {faqItems.map((item: any, i: number) => (
               <div key={i} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-colors">
                  <h3 className="font-bold text-white mb-2 flex items-start gap-3">
                     <span className="text-cyan-500 font-mono">0{i+1}.</span>
                     {item.q}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed pl-8">
                     {item.a}
                  </p>
               </div>
            ))}
         </div>
      </section>

    </div>
  );
};

export default Solutions;
